import { type ChangeEvent, useCallback } from 'react'

import { useAtom } from 'jotai'

import { FEATURE_FLAGS, type FeatureFlagDefinition } from './featureFlags'
import { featureFlagsAtom } from './featureFlags.store'

type FlagEntry = [string, FeatureFlagDefinition]

/**
 * Hook encapsulating the feature-flag toggle logic for FeatureToggle panel.
 *
 * Manages the active flags set via Jotai and exposes memoized handlers.
 * State is persisted in localStorage and therefore survives a page refresh.
 *
 * @returns flagEntries - List of registered flag keys + definitions
 * @returns activeFlags - Set of currently active flag keys
 * @returns handleToggle - Curried checkbox change handler for a given flag key
 *
 * @example
 * ```ts
 * const { flagEntries, activeFlags, handleToggle } = useFeatureToggle()
 * ```
 */
export const useFeatureToggle = () => {
  const [activeFlags, setActiveFlags] = useAtom(featureFlagsAtom)

  const flagEntries = Object.entries(FEATURE_FLAGS) as FlagEntry[]

  const handleToggle = useCallback(
    (key: string) =>
      (e: ChangeEvent<HTMLInputElement>): void => {
        setActiveFlags(prev => {
          const next = new Set(prev)
          if (e.target.checked) {
            next.add(key)
          } else {
            next.delete(key)
          }
          return next
        })
      },
    [setActiveFlags],
  )

  return {
    flagEntries,
    activeFlags,
    handleToggle,
  }
}
